import {
  defaultImportProfileKey,
  type IntegrationOperation,
  type ProviderImportProfile,
  resolveImportProfileKey,
  type SyncSettingConfig,
} from '@timelyapp/integration-cloud-api-spec';
import { checkIsAccountRoleElevatedStatus } from '@timelyapp/integration-cloud-sdk/utils';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { saveSubscriptionChanges as saveSubscriptionChangesThunk } from '~domains/integrations/redux/tic.integrations.actions';
import {
  getIntegrationConfiguration,
  getPendingChangesState,
  getSubscriptionCounts,
} from '~domains/integrations/redux/tic.integrations.selectors';
import {
  clearConfiguration,
  clearPendingChanges,
  type IntegrationConfiguration,
  type PendingSubscriptionChange,
  setConfiguration,
} from '~domains/integrations/redux/tic.integrations.slice';
import { useWithDispatch } from '~hooks/useWithDispatch';
import type { GetIntegrationDetailsApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { getIsUserInExperimentalFeatureGroup } from '~redux/selectors/user';
import { EMPTY_ARRAY } from '../../../constants/defaults';
import { buildImportSummaries, type ImportSummary } from '../utils/util.importSummaries';
import { processSyncSettings, type SyncSettingsReturns } from '../utils/util.syncSettings';
import { useActiveImportProfile } from './useActiveImportProfile';

// Note: We use the actual types from the API response rather than recreating them.
// The data shape is determined by the scope ('personal' | 'account') at runtime.

type ScopeData =
  | GetIntegrationDetailsApiResponse['data']['personal']
  | GetIntegrationDetailsApiResponse['data']['account'];

const EMPTY_SETTINGS: Record<string, never> = {};

// The hook return type with proper typing
export interface UseIntegrationModalManager {
  active: IntegrationConfiguration & {
    workspaceAccountId: string | null;
    userId: string | null;
    connection: NonNullable<ScopeData>['connection'] | null | undefined;
    scopeOperations: NonNullable<ScopeData>['operations'];
    scopeData: ScopeData | null;
    resolvedImportProfileKey: string | null;
    activeProfile: ProviderImportProfile | undefined;
    processedSyncSettings: SyncSettingsReturns;
    importSummaries: ImportSummary[];
  };
  providerTitle: string | null;
  integrationDetails: GetIntegrationDetailsApiResponse['data'] | undefined;
  setConfig: (config: Partial<IntegrationConfiguration>) => void;
  clearConfig: () => void;
  reloadData: () => void;
  isModalOpen: boolean;
  canManageWorkspace: boolean;
  isEarlyAdopter: boolean;
  isLoading: boolean;
  /** RTK Query is actively fetching `getIntegrationDetails` (including refetches). */
  isFetchingIntegrationDetails: boolean;
  /** Trigger a refetch of integration details. Returns a promise that resolves when data arrives. */
  refetchIntegrationDetails: () => Promise<unknown>;
  subscriptionStats: {
    totalCount: number;
    enabledCount: number;
    pendingChangesCount: number;
    hasChanges: boolean;
    canSave: boolean;
    pendingChangesForSave: PendingSubscriptionChange[];
  };
  saveSubscriptionChanges: () => Promise<void>;
  clearSubscriptionChanges: () => void;
}

// Level 1: Has integration details but connection may not exist yet (for initial setup)
export type UseIntegrationModalManagerWithDetails = Omit<
  UseIntegrationModalManager,
  'integrationDetails' | 'active'
> & {
  active: UseIntegrationModalManager['active'] & {
    toolId: string;
    userId: string;
    workspaceAccountId: string;
    // connection can still be null/undefined during initial setup
  };
  integrationDetails: NonNullable<GetIntegrationDetailsApiResponse['data']>;
};

// Level 2: Full guarantee with connection (for post-auth steps)
export type UseIntegrationModalManagerGuaranteed = Omit<
  UseIntegrationModalManagerWithDetails,
  'active'
> & {
  active: UseIntegrationModalManagerWithDetails['active'] & {
    connection: NonNullable<UseIntegrationModalManager['active']['connection']>;
  };
};

// Type guard to check if integration details are available (safe for auth step)
export function isDetailsAvailable(
  manager: UseIntegrationModalManager
): manager is UseIntegrationModalManagerWithDetails {
  return !!(
    manager.active.toolId &&
    manager.active.userId &&
    manager.active.workspaceAccountId &&
    manager.integrationDetails
  );
}

// Type guard to check if full data is guaranteed (required for post-auth steps)
export function isGuaranteedData(
  manager: UseIntegrationModalManager
): manager is UseIntegrationModalManagerGuaranteed {
  return !!(isDetailsAvailable(manager) && manager.active.connection);
}

// NOTE: UseIntegrationModalManagerReturn and UseIntegrationModalManagerWithConnection
// have been deprecated. Use UseIntegrationModalManager directly.

/**
 * Central state management layer for integration modals and setup wizards.
 *
 * Responsibilities:
 * - Fetch integration details and scope-resolve connection data
 * - Resolve active import profile key and provide the profile metadata
 * - Expose memoized sync settings derived from provider operations
 * - Surface import feature summaries for UI copy
 * - Wire up subscription statistics/mutations and modal state helpers
 */
export function useIntegrationModalManager(): UseIntegrationModalManager {
  const configuration = useSelector(getIntegrationConfiguration);
  const toolId = configuration.toolId;
  const { workspace, user } = useSelector(getContextualTicAccounts);
  const isEarlyAdopter = useSelector(getIsUserInExperimentalFeatureGroup);

  // Get focused subscription data
  const subscriptionCounts = useSelector(getSubscriptionCounts);
  const pendingChangesState = useSelector(getPendingChangesState);

  const actions = useWithDispatch({
    setConfiguration,
    clearConfiguration,
    clearPendingChanges,
    invalidateTags: ticApi.util.invalidateTags,
    saveSubscriptionChangesThunk,
  });

  const skip = !workspace?.id || !toolId;
  const {
    data: providerRes,
    isLoading,
    isFetching: isFetchingIntegrationDetails,
    refetch: refetchIntegrationDetails,
  } = ticApi.useGetIntegrationDetailsQuery(
    { accountId: workspace?.id!, providerId: toolId! },
    { skip, refetchOnMountOrArgChange: true }
  );

  const integrationDetails = providerRes?.data;
  const importProfiles = integrationDetails?.import_profiles;
  const canManageWorkspace = workspace?.role
    ? checkIsAccountRoleElevatedStatus(workspace.role)
    : false;

  // Simple scope logic - trust user permissions and configuration
  const scope = configuration.scope || (canManageWorkspace ? 'account' : 'personal');
  const connection = integrationDetails
    ? scope === 'personal'
      ? integrationDetails.personal?.connection
      : integrationDetails.account?.connection
    : null;

  const clearConfig = useCallback(() => {
    actions.clearConfiguration();
    actions.invalidateTags(['IntegrationDetails', 'Integrations']);
  }, [actions]);

  const reloadData = useCallback(() => {
    actions.invalidateTags(['IntegrationDetails', 'Integrations']);
  }, [actions]);

  const saveSubscriptionChanges = useCallback(async () => {
    await actions.saveSubscriptionChangesThunk().unwrap();
  }, [actions]);

  const clearSubscriptionChanges = useCallback(() => {
    actions.clearPendingChanges();
  }, [actions]);

  // Compute scope-specific data for active object
  const scopeData = integrationDetails
    ? scope === 'personal'
      ? integrationDetails.personal
      : integrationDetails.account
    : null;

  const scopeOperations = scopeData?.operations || EMPTY_ARRAY;
  const defaultProfileKey = useMemo(
    () => defaultImportProfileKey(importProfiles),
    [importProfiles]
  );

  const resolvedImportProfileKey = useMemo(
    () =>
      resolveImportProfileKey({
        scope,
        selectedProfileKey: configuration.importProfileKey ?? null,
        connectionProfileKey: connection?.settings?.import_profile_key ?? null,
        defaultProfileKey,
      }),
    [
      scope,
      configuration.importProfileKey,
      connection?.settings?.import_profile_key,
      defaultProfileKey,
    ]
  );

  const activeProfile = useActiveImportProfile(importProfiles, resolvedImportProfileKey);
  const providerTitle = integrationDetails?.title || configuration.toolId || null;

  const syncSettings = (integrationDetails?.sync_settings || EMPTY_ARRAY) as SyncSettingConfig[];
  const processedSyncSettings = useMemo(() => {
    const connectionSettings =
      connection?.settings ?? scopeData?.connection?.settings ?? EMPTY_SETTINGS;
    return processSyncSettings(syncSettings, connectionSettings);
  }, [syncSettings, connection?.settings, scopeData?.connection?.settings]);

  const importSummaries = useMemo(
    () =>
      buildImportSummaries({
        operations: (scopeOperations || []) as IntegrationOperation[],
        activeProfile,
        providerTitle,
      }),
    [scopeOperations, activeProfile, providerTitle]
  );

  return {
    active: {
      ...configuration,
      workspaceAccountId: workspace?.id || null, // Rename to be clear this is workspace, not connection
      userId: user?.id || null,
      connection, // Provide explicit connection property for component access
      // Add computed properties for direct component access
      scopeOperations,
      scopeData,
      resolvedImportProfileKey,
      activeProfile,
      processedSyncSettings,
      importSummaries,
    },
    providerTitle,
    integrationDetails,
    setConfig: actions.setConfiguration,
    clearConfig,
    reloadData,
    isModalOpen: !!configuration.modal,
    canManageWorkspace,
    isEarlyAdopter,
    isLoading,
    isFetchingIntegrationDetails,
    refetchIntegrationDetails,
    subscriptionStats: {
      totalCount: subscriptionCounts.total,
      enabledCount: subscriptionCounts.enabled,
      pendingChangesCount: pendingChangesState.count,
      hasChanges: pendingChangesState.hasChanges,
      canSave: pendingChangesState.canSave,
      pendingChangesForSave: pendingChangesState.forSave,
    },
    saveSubscriptionChanges,
    clearSubscriptionChanges,
  };
}
